import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import get from 'lodash/get';
import { useRouteParams } from '../../hooks/useRouteParams';
import { useSaveDraftMutation } from '../../api/useSaveDraftMutation';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { DraftAccountDTO } from '../../AccountOpening.meta';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Footer } from '../../components/Footer/Footer';
import { useDraftAccountCache } from '../../api/useDraftAccountQuery';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import {
  FormValues,
  parseDefaultValues,
  parseSubmittedValues,
  validation,
} from './SimpleIraPlanName.meta';
import { StepCard } from '../../components/StepCard/StepCard';
import { StepContent } from '../../components/StepContent/StepContent';
import { GliaGlobalStyle } from '../../AccountOpening.styles';

const fieldName = 'simpleIraPlanName';

interface Props {
  draftAccount: DraftAccountDTO;
}

export const SimpleIraPlanName: React.FC<Props> = ({ draftAccount }) => {
  const formBag = useForm<FormValues>({
    defaultValues: parseDefaultValues(draftAccount),
    resolver: yupResolver(validation),
  });

  const {
    formState: { errors },
  } = formBag;

  const { clientId, draftId } = useRouteParams();

  const { mutateDraftAccount } = useDraftAccountCache();

  const {
    mutateAsync: saveDraft,
    isLoading: isSavingDraft,
    error: saveDraftError,
  } = useSaveDraftMutation();

  const onSubmit = async values => {
    const parsedValues = parseSubmittedValues(values, draftAccount);

    try {
      await saveDraft({ clientId, id: draftId, data: { ...draftAccount, ...parsedValues } });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <GliaGlobalStyle />

      <Box as="form" onSubmit={formBag.handleSubmit(onSubmit)} minWidth={864} mt={96}>
        {saveDraftError && (
          <Box maxWidth="800px" mx="auto">
            <Alert message={saveDraftError.message} variant="error" mb="32" />
          </Box>
        )}

        <StepContent>
          <StepCard>
            <Text color="neutral.700" fontSize="h3" fontWeight={500} mb="32">
              What is your Simple IRA plan name?
            </Text>

            <Input
              {...formBag.register(fieldName)}
              label="Plan Name"
              formVariant={get(errors, fieldName) ? 'error' : undefined}
              helperText={get(errors, fieldName)?.message}
              type="text"
              maxLength={256}
            />
          </StepCard>
        </StepContent>

        <Footer
          onPrevious={() => {
            mutateDraftAccount({
              accountIraType: null,
            });
          }}
          isLoading={isSavingDraft}
        />
      </Box>
    </>
  );
};
